/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import type { ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	wrapper: {
		boxSizing: 'border-box',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
	},
	iconContainer: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	content: {
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.100'),
		flex: 1,
	},
	buttonGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	closeButton: {
		marginInlineStart: token('space.100'),
	},
});

export type RovoThinkingErrorProps = {
	/** Error message to display. Falls back to a generic "unknown error". */
	errorText?: ReactNode;
	/** Called when the user dismisses the error bar. */
	onClose: () => void;
	/** Called on retry. When omitted, the "Try again" button is hidden. */
	onTryAgain?: () => void;
};

/** Thinking bar error screen: danger icon, message, and Close / Try again. */
export function RovoThinkingError({
	errorText,
	onClose,
	onTryAgain,
}: RovoThinkingErrorProps): JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.wrapper}>
			<div css={styles.content}>
				<div css={styles.iconContainer}>
					<StatusErrorIcon label="" color={token('color.icon.danger')} />
				</div>
				<Text>{errorText ?? formatMessage(messages.thinkingErrorMessage)}</Text>
			</div>
			<div css={styles.buttonGroup}>
				<span css={styles.closeButton}>
					{onTryAgain ? (
						<Button appearance="default" onClick={onClose} testId="thinking-error-close-button">
							{formatMessage(messages.thinkingClose)}
						</Button>
					) : (
						<IconButton
							appearance="subtle"
							spacing="compact"
							onClick={onClose}
							testId="thinking-error-close-button"
							label={formatMessage(messages.thinkingClose)}
							icon={CrossIcon}
						/>
					)}
				</span>
				{onTryAgain && (
					<Button
						appearance="primary"
						onClick={onTryAgain}
						testId="thinking-error-try-again-button"
					>
						{formatMessage(messages.thinkingErrorTryAgain)}
					</Button>
				)}
			</div>
		</div>
	);
}
